// imports
import { useDispatch, useSelector } from 'react-redux';
import { Box, styled } from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

// local imports
import { RootState, uiActions } from '../../store';
import useHuc12Banner from '../../hooks/useHuc12Banner';

interface BannerProps {
  left: string;
}

/**
 * Banner
 * Renders a list of banner components in a container
 *
 * @param {BannerProps} props - Styles for the container
 * @returns {JSX.Element} The rendered banner components wrapped in a container
 */
const BannerContainer = styled(Box)<{ left: string }>(({ left, theme }) => ({
  zIndex: 50,
  position: 'absolute',
  top: 10,
  left: left,
  gap: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
}));

const BannerMessage = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '#fcf4d4',
  borderRadius: '4px',
  padding: '10px 5px',
  fontSize: '1rem',
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
  gap: theme.spacing(1),
  position: 'relative',
  borderLeft: '5px solid orange',
  flexWrap: 'nowrap',
  width: 'fit-content',
}));

const CloseBtn = styled('button')({
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: '1.5rem',
  lineHeight: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  '&:hover': {
    opacity: 0.7,
  },
});

const StyledCloseIcon = styled(CloseIcon)({
  color: '#000000',
  marginTop: '2px',
  fontSize: '1.5rem',
  marginLeft: '10px',
});

const ViewDataBanner = ({ left }: BannerProps) => {
  useHuc12Banner();

  const dispatch = useDispatch();
  const bannerMessages = useSelector(
    (state: RootState) => state.ui.bannerMessages
  );
  const sidePanelOpen = useSelector(
    (state: RootState) => state.map.sidePanelOpen
  );

  const closeBanner = (id: string) => {
    dispatch(uiActions.removeBannerMessage(id));
  };

  return (
    <BannerContainer
      left={left}
      sx={{
        // Hide banner when width is narrow AND side panel is open
        ...(sidePanelOpen && {
          '@media (max-width: 490px)': {
            display: 'none',
          },
        }),

        // Update left position based on side panel size
        ...(sidePanelOpen && {
          '@media (max-width: 649px)': {
            left: '260px',
          },
          '@media (min-width: 650px)': {
            left: '410px',
          },
        }),
      }}
    >
      {bannerMessages.map((msg) => (
        <BannerMessage
          key={msg.id}
          sx={{
            // When side panel is open: maxWidth = screen width - side panel width and gap - 55px gap between banner and right side of screen
            ...(sidePanelOpen && {
              '@media (min-width: 650px) and (max-width: 960px)': {
                maxWidth: 'calc(100vw - 410px - 55px)',
              },
              '@media (max-width: 649px)': {
                maxWidth: 'calc(100vw - 260px - 55px)',
              },
            }),
            // When side panel is closed: maxWidth = screen width - gap between banner and left edge - gap between banner and right edge
            ...(!sidePanelOpen && {
              maxWidth: 'calc(100vw - 10px - 55px)',
            }),
          }}
        >
          <InfoOutlineIcon sx={{ fontSize: '1.5rem', flexShrink: 0 }} />
          <span style={{ flexGrow: 1, minWidth: 0, wordBreak: 'break-word' }}>
            {msg.message}
          </span>
          <CloseBtn
            onClick={() => closeBanner(msg.id)}
            aria-label="Close HUC banner"
            title="Close HUC banner"
          >
            <StyledCloseIcon />
          </CloseBtn>
        </BannerMessage>
      ))}
    </BannerContainer>
  );
};

export default ViewDataBanner;
